import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Plus, Search, ChevronLeft, ChevronRight, 
  RefreshCw, Building2, Filter, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const VendorList = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canCreate } = usePermissions('vendors');
  
  // فلاتر البحث والباجينشن (Server-side)
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorType, setVendorType] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showAllSites, setShowAllSites] = useState(false);
  const [searchParams] = useSearchParams();
  const itemsPerPage = 20;
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [filters, setFilters] = useState({
        comp_num: "",
        name: "",
        type: "",
        currency: "",
        org_id: ""
    });

  // 1. دالة جلب البيانات مع دعم الباجينيشن والفلاتر
  const fetchVendors = useCallback(async () => {
  setLoading(true);
  const token = localStorage.getItem('token');
  const skip = (currentPage - 1) * itemsPerPage;
  
  try {
    const res = await axios.get('/api/companies', {
      params: {
        all_sites: Boolean(showAllSites),
        org_id: sessionOrg,
        skip: skip,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        // 🎯 حقن الفلاتر التفصيلية هنا
        comp_num: filters.comp_num || undefined,
        name: filters.name || undefined,
        type: filters.type || filters.type_filter || undefined, // دعم النوعين
        currency: filters.currency || undefined,
        org_filter: filters.org_id || undefined
      },
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    setVendors(res.data.items);
    setTotalPages(res.data.pages);
    setTotalItems(res.data.total);
  } catch (err) {
    toast.error("Error loading vendors");
  } finally {
    setLoading(false);
  }
}, [showAllSites, currentPage, searchTerm, filters]); // 👈 لازم تضيف filters هنا كـ dependency

  // تحديث البيانات عند تغيير الصفحة أو الفلاتر
  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1); // ارجع لأول صفحة عند الفلترة
    };

  const handleRowClick = (compnum) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            all_sites: showAllSites,
            org_id: sessionOrg
        }).toString();
        navigate(`/vendors/${compnum}?${queryParams}`);
    };

  // إعادة ضبط الصفحة عند البحث الجديد
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); 
  };

  const handleTypeChange = (e) => {
    setVendorType(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
        const openAllSites = () => { 
            setShowAllSites(true); 
            setPage(1); 
        };
        const viewMySite = () => { 
            setShowAllSites(false); 
            setPage(1); 
        };
        
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        // ...
    }, []);

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6] text-[11px] font-sans overflow-hidden" style={{ fontSize: `${fontSize}px` }}>
      
      {/* --- Toolbar --- */}
      <div className="bg-[#1a3353] p-3 shadow-lg z-20">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center gap-6">
            <h2 className="text-white font-black italic uppercase text-lg flex items-center gap-3">
              <Building2 size={24} className="text-blue-400" /> 
              <span>Companies</span>
              <span className="bg-blue-600 text-[10px] px-2 py-0.5 rounded-full not-italic ml-2">{totalItems}</span>
            </h2>
            
            <div className="relative group">
              <Search className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-blue-400" size={14} />
              <input 
                className="pl-9 pr-4 py-2 bg-[#ffffff10] border border-white/10 w-96 outline-none text-white focus:bg-white focus:text-black focus:border-blue-500 shadow-inner rounded-md transition-all placeholder:text-gray-500" 
                placeholder="Search by ID, Name or TRN..." 
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="flex items-center gap-2 bg-white/5 p-1 rounded-md border border-white/10">
                <Filter size={12} className="text-gray-400 ml-2"/>
                <select 
                  className="bg-transparent text-gray-300 border-none p-1 text-[10px] font-bold uppercase outline-none cursor-pointer hover:text-white"
                  value={vendorType}
                  onChange={handleTypeChange}
                >
                  <option value="" className="bg-[#1a3353]">All Types</option>
                  <option value="VENDOR" className="bg-[#1a3353]">Vendor</option>
                  <option value="MANUFACTURER" className="bg-[#1a3353]">Manufacturer</option>
                </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={fetchVendors} className="p-2 text-blue-300 hover:bg-white/10 rounded-full transition-all">
               <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => navigate('/vendors/new')} className="bg-blue-600 text-white px-6 py-2 rounded-md font-black flex items-center gap-2 uppercase hover:bg-blue-500 shadow-xl active:scale-95 transition-all">
              <Plus size={16} /> New Company
            </button>
          </div>
        </div>
      </div>

      {/* --- Table Area --- */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b text-slate-500 font-black uppercase text-[9px] sticky top-0 z-10">
              <tr>
                <th className="p-4 border-r w-32 tracking-widest italic">Company ID</th>
                <th className="p-4 border-r tracking-widest italic">Company Name</th>
                <th className="p-4 border-r w-32 tracking-widest italic text-center">Type</th>
                <th className="p-4 border-r w-24 tracking-widest italic text-center">Currency</th>
                <th className="p-4 border-r w-40 tracking-widest italic">TRN</th>
                <th className="p-4 tracking-widest italic">Organization</th>
              </tr>
              {/* 🎯 Filter Row (نفس شكل الـ WO) */}
              <tr className="bg-[#f8fafc]">
                  <td className="p-1 border-r border-gray-200">
                      <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white" value={filters.comp_num} onChange={e => handleFilterChange('comp_num', e.target.value)} />
                  </td>
                  <td className="p-1 border-r border-gray-200">
                      <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white" value={filters.name} onChange={e => handleFilterChange('name', e.target.value)} />
                  </td>
                  <td className="p-1 border-r border-gray-200">
                      <select className="w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white" value={filters.type} onChange={e => handleFilterChange('type', e.target.value)}>
                          <option value="" className="bg-[#1a3353]">All Types</option>
                          <option value="VENDOR" className="bg-[#1a3353]">Vendor</option>
                          <option value="MANUFACTURER" className="bg-[#1a3353]">Manufacturer</option>
                      </select>
                  </td>
                  <td className="p-1 border-r border-gray-200 bg-gray-100/50"></td>
                  <td className="p-1 border-r border-gray-200 bg-gray-100/50"></td>
                  <td className="p-1 border-gray-200">
                      <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white text-center" value={filters.org_id} onChange={e => handleFilterChange('org_id', e.target.value)} />
                  </td>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-20 text-center">
                    <div className="flex flex-col items-center gap-4 text-slate-300 italic">
                      <RefreshCw size={40} className="animate-spin text-blue-100" />
                      Loading Company Directory...
                    </div>
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr><td colSpan="6" className="p-20 text-center text-slate-400 italic">No companies matching your search.</td></tr>
              ) : vendors.map((v) => (
                <tr key={v.comp_id} className="hover:bg-blue-50/50 cursor-pointer transition-colors group" onClick={() => handleRowClick(v.comp_num)}>
                  <td className="p-4 border-r font-black text-blue-900 uppercase group-hover:text-blue-600">{v.comp_num}</td>
                  <td className="p-4 border-r font-bold text-slate-700">{v.name}</td>
                  <td className="p-4 border-r text-center">
                    <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-tighter ${v.type === 'VENDOR' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}`}>
                      {v.type}
                    </span>
                  </td>
                  <td className="p-4 border-r text-center text-slate-500 font-black">{v.currency}</td>
                  <td className="p-4 border-r text-slate-400 font-mono">{v.trn || '---'}</td>
                  <td className="p-4 text-slate-400 font-black italic">{v.org_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Pagination Footer --- */}
      <div className="bg-white border-t p-3 flex justify-between items-center px-8 shadow-inner shrink-0">
        <div className="text-slate-400 font-bold text-[10px] uppercase italic">
          Showing <span className="text-slate-700">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="text-slate-700">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="text-blue-600">{totalItems}</span> Records
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
            className="p-2 border rounded hover:bg-slate-50 disabled:opacity-30 transition-all text-slate-600"
          ><ChevronsLeft size={14}/></button>
          
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
            className="p-2 border rounded hover:bg-slate-50 disabled:opacity-30 transition-all text-slate-600"
          ><ChevronLeft size={14}/></button>
          
          <div className="flex gap-1 mx-2">
             {[...Array(totalPages)].map((_, i) => {
               const p = i + 1;
               // إظهار أول 3 صفحات وآخر 3 صفحات والحالية لعدم زحمة الأرقام
               if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                 return (
                   <button 
                     key={p}
                     onClick={() => setCurrentPage(p)}
                     className={`w-8 h-8 rounded-md font-black text-[10px] transition-all border
                     ${currentPage === p ? 'bg-blue-900 text-white border-blue-900 shadow-md' : 'bg-white text-slate-400 hover:border-blue-900'}`}
                   >{p}</button>
                 );
               }
               if (p === currentPage - 2 || p === currentPage + 2) return <span key={p} className="px-1 text-slate-300">...</span>;
               return null;
             })}
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="p-2 border rounded hover:bg-slate-50 disabled:opacity-30 transition-all text-slate-600"
          ><ChevronRight size={14}/></button>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
            className="p-2 border rounded hover:bg-slate-50 disabled:opacity-30 transition-all text-slate-600"
          ><ChevronsRight size={14}/></button>
        </div>
      </div>
    </div>
  );
};

export default VendorList;